import { TransitionType } from '../../types/state'
import { VideoType } from '../constants/enums'
import { APPSTATE, ROOMSSTATE, TransitionTypes, VIDEOSTATE } from '../state'
import { getMenuState, getModalState } from '../state/getState'
import { closeDialog, openModelMenuDialog } from '../state/modals/state.dialog'
import { closeMiniPlayers, openMiniPlayer } from '../state/modals/state.floater'
import { setMenuMuteState } from '../state/state.menu'
import { initVideo, setVideoMuteState } from '../state/state.video'
import { safeObj } from '../utilities/safe'
import { motionTransition } from '../utilities/transitions'
import { loadUserIfNeeded } from './loadUserIfNeeded'

const onMenuMute = (isMuted: boolean) => {
    const menuState = getMenuState()
    const mute = menuState?.persist?.mute

    if (isMuted === mute) {
        return
    }

    if (mute === undefined) {
        setVideoMuteState(isMuted)
        return
    }

    // The viewer unmuted a preview the menu started muted. Later menus follow
    // the global mute, and so does the room the viewer opens from here: the
    // room route syncs its player to it.
    setMenuMuteState(undefined)
    setVideoMuteState(isMuted)
}

// The open room's player (a lounge plays its surrogate's) while the room
// renders it. The menu then shows that same player, so it keeps the room's
// options: the menu's muted start would not apply to it anyway, and with
// onMenuMute the viewer's mute there never reached the global mute, so the
// room route turned it back.
const isRoomPlayer = (id: number) => {
    const { show, id: roomID } = getModalState().floating_room
    if (!show) return false
    const room = ROOMSSTATE.rooms[roomID]
    const playerID = room?.isLounge ? room.loungeSurrogateID : roomID
    return playerID === id && !!VIDEOSTATE.videos[id]?.placeholders.length
}

export const openMenuDialog = async (
    slugOrId: string | number,
    props: {
        hideNextPrev?: boolean
        message?: boolean
        noVideo?: boolean
        room?: boolean
        transitionSource?: TransitionType
    } = {},
) => {
    const user = await loadUserIfNeeded(slugOrId)
    const { noVideo, transitionSource } = props
    const {
        isAway,
        isModel,
        isClubShow,
        isGroupShow,
        isPrivateShow,
        isTruePrivate,
        isBroadcasting,
        streamType,
        slug,
        id,
    } = safeObj(user)
    motionTransition(
        () => {
            // a phone keeps one menu player: opening any menu closes it, and
            // reopening its model's menu is its restore
            if (APPSTATE.app.isMobile) closeMiniPlayers('menu')
            const menuState = getMenuState()
            const mute = menuState?.persist?.mute
            if (isModel === false) {
                openModelMenuDialog(slug, props)
                return
            }

            const inShow = streamType !== 'public'
            const hiddenShow =
                (isTruePrivate ||
                    isPrivateShow ||
                    isGroupShow ||
                    isClubShow ||
                    isAway) &&
                !inShow

            if (
                !noVideo &&
                isBroadcasting &&
                !hiddenShow &&
                !isRoomPlayer(id)
            ) {
                initVideo(slug, {
                    videoType: VideoType.Menu,
                    onMute: onMenuMute,
                    mute,
                })
            }
            openModelMenuDialog(slug, props)
        },
        {
            id,
            skipTransition: getModalState().dialog.show,
            transitionSource,
            transitionDestination: TransitionTypes.MENU,
        },
    )
}

/** The menu shrinks to its model's mini player; a drag drops it at `coords` */
export const minimizeMenu = (id: number, coords?: { x: number; y: number }) =>
    motionTransition(
        () => {
            closeDialog(true)
            openMiniPlayer(id, { kind: 'menu' }, coords)
        },
        {
            id,
            skipTransition: !!coords,
            transitionSource: TransitionTypes.MENU,
            transitionDestination: TransitionTypes.MINI_PLAYER,
        },
    )
